//app/frontend/components/PrivateLessons/LessonDetailsForm.tsx

import React from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Textarea, Select, Button, Paper, Group, Stack } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import AddressAutocomplete from '../../components/AddressAutocompleteMap';

interface PrivateLessonPage {
  id: string;
  private_lesson_type_id: string;
  event_id: string;
  description: string;
  custom_location: string;
  additional_info: string;
  timezone: string;
  user: {
    username: string;
  };
  artist: {
    name: string;
    slug: string;
  };
  slug: string;
}

interface PrivateLessonType {
  id: string;
  name: string;
}

interface Event {
  id: string;
  name: string;
}

interface LessonDetailsFormProps {
  lessonPage: PrivateLessonPage;
  onSubmit: (data: Partial<PrivateLessonPage>) => void;
  isLoading: boolean;
  availableLessonTypes: PrivateLessonType[];
  availableEvents: Event[];
}

export const LessonDetailsForm: React.FC<LessonDetailsFormProps> = ({
  lessonPage,
  onSubmit,
  isLoading,
  availableLessonTypes = [],
  availableEvents = [],
}) => {
  // Initialize form with existing values
  const form = useForm({
    initialValues: {
      private_lesson_type_id: lessonPage.private_lesson_type_id ? String(lessonPage.private_lesson_type_id) : '',
      event_id: lessonPage.event_id ? String(lessonPage.event_id) : '',
      description: lessonPage.description || '',
      custom_location: lessonPage.custom_location || '',
      additional_info: lessonPage.additional_info || '',
      timezone: lessonPage.timezone || '',
    },
    validate: {
      private_lesson_type_id: (value) => (!value ? 'Lesson type is required' : null),
      description: (value) => (!value ? 'Description is required' : null),
      timezone: (value) => (!value ? 'Timezone is required' : null),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    onSubmit({
      ...values,
      id: lessonPage.id,
    });
  };

  // Handle address selection from Google Places
  const handleAddressChange = (address: string) => {
    form.setFieldValue('custom_location', address);
  };

  // Handle timezone selection
  const handleTimezoneChange = (timezone: string) => {
    form.setFieldValue('timezone', timezone);
  };

  return (
    <Paper p="md" pos="relative" shadow="md" style={{ width: '100%', backgroundColor: '#757575' }}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack
          gap="lg"
          color="gray"
          align="stretch"
          justify="flex-start"
          style={{ width: '100%', backgroundColor: '#757575' }}
        >
          <Select
            label="Private Lesson Type"
            placeholder="Select Private Lesson Type"
            data={availableLessonTypes.map((type) => ({
              value: String(type.id),
              label: type.name,
            }))}
            required
            {...form.getInputProps('private_lesson_type_id')}
            searchable
            labelProps={{
              style: {
                display: 'block',
                textAlign: 'left',
                marginBottom: '5px',
                color: 'white',
              },
            }}
          />

          <Select
            label="Associated Event"
            placeholder="Select Event"
            data={availableEvents.map((event) => ({
              value: String(event.id),
              label: event.name,
            }))}
            {...form.getInputProps('event_id')}
            searchable
            clearable
            labelProps={{
              style: {
                display: 'block',
                textAlign: 'left',
                marginBottom: '5px',
                color: 'white',
              },
            }}
          />

          <Textarea
            label="Description"
            placeholder="Enter lesson description"
            minRows={4}
            required
            {...form.getInputProps('description')}
            labelProps={{
              style: {
                display: 'block',
                textAlign: 'left',
                marginBottom: '5px',
                color: 'white',
              },
            }}
          />

          {/* Address Autocomplete and Timezone Component */}
          <AddressAutocomplete
            value={form.values.custom_location}
            onChange={handleAddressChange}
            onTimezoneSelect={handleTimezoneChange}
            timezoneValue={form.values.timezone}
          />

          <Textarea
            label="Additional Information"
            placeholder="Enter any additional information for students"
            minRows={3}
            {...form.getInputProps('additional_info')}
            labelProps={{
              style: {
                display: 'block',
                textAlign: 'left',
                marginBottom: '5px',
                color: 'white',
              },
            }}
          />

          <Group gap="right" mt="md">
            <Button
              type="submit"
              color="orange"
              size="lg"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={isLoading}
            >
              Save Lesson Details
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};
